import type { CardId, EntityId } from '../engine/types';

export type DiagnosticPhase = 'command' | 'simulation' | 'bot' | 'presentation' | 'performance';
export type DiagnosticLevel = 'debug' | 'info' | 'warn' | 'error';
export type DiagnosticProfile = 'full' | 'standard' | 'off';

export interface DiagnosticRecord<T = unknown> {
  schemaVersion: 1;
  matchId: string;
  eventId: number;
  tick: number;
  phase: DiagnosticPhase;
  source: string;
  level: DiagnosticLevel;
  code: string;
  commandSequence?: number;
  entityIds?: readonly EntityId[];
  cardIds?: readonly CardId[];
  correlationId?: string;
  payload: T;
}

export interface DiagnosticInput<T = unknown> {
  tick: number;
  phase: DiagnosticPhase;
  source: string;
  level?: DiagnosticLevel;
  code: string;
  commandSequence?: number;
  entityIds?: readonly EntityId[];
  cardIds?: readonly CardId[];
  correlationId?: string;
  payload?: T;
}

export class DiagnosticJournal {
  readonly records: DiagnosticRecord[] = [];
  droppedRecords = 0;
  sinkErrors = 0;
  private nextEventId = 1;

  constructor(readonly matchId: string, readonly profile: DiagnosticProfile = 'full', readonly maxRecords = 200_000) {}

  record<T>(input: DiagnosticInput<T>): DiagnosticRecord<T> | null {
    if (this.profile === 'off' && input.level !== 'error') return null;
    if (this.profile === 'standard' && input.level === 'debug') return null;
    if (this.records.length >= this.maxRecords) {
      this.droppedRecords += 1;
      return null;
    }
    const base = {
      schemaVersion: 1 as const,
      matchId: this.matchId,
      eventId: this.nextEventId++,
      tick: input.tick,
      phase: input.phase,
      source: input.source,
      level: input.level ?? 'info',
      code: input.code,
      payload: input.payload ?? ({} as T),
    };
    const optional = {
      ...(input.commandSequence === undefined ? {} : { commandSequence: input.commandSequence }),
      ...(input.entityIds === undefined ? {} : { entityIds: input.entityIds }),
      ...(input.cardIds === undefined ? {} : { cardIds: input.cardIds }),
      ...(input.correlationId === undefined ? {} : { correlationId: input.correlationId }),
    };
    const record: DiagnosticRecord<T> = { ...base, ...optional };
    this.records.push(record as DiagnosticRecord);
    return record;
  }

  recordsForEntity(entityId: EntityId): DiagnosticRecord[] {
    return this.records.filter((record) => record.entityIds?.includes(entityId));
  }
}
